import { useEffect, useRef, useState } from 'react';

/**
 * Tracks which children of a wrapping grid start a NEW visual row, by comparing
 * each child's `offsetTop` to the first child's. Returns a ref for the grid
 * container and the set of new-row child indices, so the caller can draw a row
 * separator on wrapped items. Re-measures on container resize (ResizeObserver)
 * and whenever `itemCount` changes (cards added/removed).
 *
 * Extracted so both inventory grids (the per-pool grid and the type-card grid)
 * share one implementation instead of duplicating the observer wiring.
 */
export function useNewRowIndices(itemCount: number) {
	const ref = useRef<HTMLDivElement>(null);
	const [newRowIndices, setNewRowIndices] = useState<Set<number>>(new Set());

	useEffect(() => {
		const grid = ref.current;
		if (!grid) return;
		const measure = () => {
			const children = Array.from(grid.children) as HTMLElement[];
			if (children.length < 2) {
				setNewRowIndices(new Set());
				return;
			}
			const firstTop = children[0].offsetTop;
			const next = new Set<number>();
			for (let i = 1; i < children.length; i++) {
				if (children[i].offsetTop !== firstTop) next.add(i);
			}
			setNewRowIndices(next);
		};
		const obs = new ResizeObserver(measure);
		obs.observe(grid);
		measure();
		return () => obs.disconnect();
	}, [itemCount]);

	return { ref, newRowIndices };
}
